import { useMemo, useSyncExternalStore } from 'react'
import { api } from '../../lib/api'
import { Button, Notice } from '../../ui'
import { SalesSubmissionDraft, submissionStorageKey, type DraftStorage, type SalesSubmissionResult } from './submission'

// Keep the same draft even if its modal is closed and reopened. sessionStorage also survives a reload.
const drafts = new Map<string, SalesSubmissionDraft<object>>()
const browserStorage: DraftStorage = {
  getItem: (key) => window.sessionStorage.getItem(key),
  setItem: (key, value) => window.sessionStorage.setItem(key, value),
}
export function useSalesSubmission<Draft extends object>(scope: string, initial: Draft, endpoint: string, prepare: (values: Draft) => Record<string, unknown>) {
  const controller = useMemo(() => {
    const key = submissionStorageKey(scope)
    let draft = drafts.get(key)
    if (!draft) { draft = new SalesSubmissionDraft(key, initial, browserStorage) as SalesSubmissionDraft<object>; drafts.set(key, draft) }
    return draft as SalesSubmissionDraft<Draft>
  }, [scope])
  const state = useSyncExternalStore(controller.subscribe, controller.snapshot, controller.snapshot)
  const sender = (body: Record<string, unknown>) => api.post<SalesSubmissionResult>(endpoint, body)
  const submit = () => controller.submit(prepare, sender).catch(() => undefined)
  const retry = () => controller.retry(sender).catch(() => undefined)
  return { state, values: state.values, locked: state.phase !== 'draft', busy: state.phase === 'sending',
    edit: (values: Draft) => controller.edit(values), submit, retry,
    revise: () => controller.reviseRejected(), newDraft: (values: Draft) => controller.newDraft(values) }
}

export function SalesSubmissionFeedback({ result, failure, uncertain = false, noun = 'Request', target = 'office' }: { result: SalesSubmissionResult | null; failure?: { message: string } | null; uncertain?: boolean; noun?: string; target?: 'office' | 'manager' }) {
  return <div aria-live="polite">
    {failure && <Notice tone={uncertain ? 'warn' : 'bad'}>{failure.message}</Notice>}
    {result && <Notice tone={result.notification.status === 'held' || result.notification.status === 'pending' ? 'warn' : 'good'}>
      {noun} saved. {result.notification.status === 'held' ? `The ${target} email has not been sent. Please follow up with Briary if this is urgent.`
        : result.notification.status === 'pending' ? `The ${target} email is still waiting to be confirmed. You can check again without submitting another request.`
          : result.notification.status === 'old_hub' ? 'Office notifications stay with the current Hub until the switchover.' : `The ${target} email is queued.`}
    </Notice>}
  </div>
}

export function SalesSubmissionRecovery({ phase, captured, retry, revise, busy, notification, target = 'office' }: {
  phase: string; captured: boolean; retry: () => unknown; revise: () => unknown; busy: boolean; notification?: SalesSubmissionResult['notification']; target?: 'office' | 'manager'
}) {
  return <div className="row" style={{ flexWrap: 'wrap' }}>
    {(phase === 'uncertain' || phase === 'rejected') && captured && <Button disabled={busy} onClick={() => { void retry() }}>Retry original request</Button>}
    {phase === 'rejected' && <Button disabled={busy} onClick={() => { void revise() }}>Edit details</Button>}
    {phase === 'saved' && notification && ['held', 'pending'].includes(notification.status) && <Button disabled={busy} onClick={() => { void retry() }}>Check {target} email again</Button>}
  </div>
}
